// Profile Panel — FLEXX trader identity card with 6-axis radar, badges,
// and NextGen Funding status. Mounted when tab === "profile".
import React, { useMemo, useState, useEffect } from "react";
import { THEME, flexxState, levelFor } from "./state";
import { useAuth } from "../contexts/AuthContext";
import PushNotifications from "../components/PushNotifications";
import InstallAppButton from "../components/InstallAppButton";
import { TIER3_EMERALD } from "./LessonsHub.Tier3";
import LeaderboardCard from "./LeaderboardCard";
import Mt5BridgePanel from "./Mt5BridgePanel";
import ChangePasswordCard from "./ChangePasswordCard";

const SAGEMASTER_URL = "https://sagemaster.com";
const SAGEMASTER_TOOLS = [
  { id: "ai-screener",      icon: "🔎", name: "AI Screener",       desc: "Pre-session ticker shortlist" },
  { id: "magic-terminal",   icon: "🧪", name: "Magic Terminal",    desc: "AI-driven setup execution" },
  { id: "helix-analytics",  icon: "🌀", name: "Helix Analytics",   desc: "Sunday weekly market report" },
  { id: "deepsage",         icon: "🤖", name: "DeepSage",          desc: "AI co-pilot for every trade" },
  { id: "ai-grid-bot",      icon: "🧮", name: "AI Grid Bot",       desc: "Range-market grid execution" },
  { id: "ai-dca-bot",       icon: "📈", name: "AI DCA Bot",        desc: "Dollar-cost averaging engine" },
  { id: "ai-omni-bot",      icon: "♾️", name: "AI Omni Bot",       desc: "Multi-strategy meta-bot" },
  { id: "ai-strategy-pack", icon: "📦", name: "AI Strategy Pack",  desc: "Curated edge templates" },
  { id: "ai-backtest",      icon: "⏪", name: "AI Backtest",       desc: "Historical strategy validator" },
];

// 6 skill axes — 5 from diagnostic + "Discipline" derived from journal grade.
const AXES = [
  { id: "marketStructure", label: "Structure" },
  { id: "ta",              label: "Technical" },
  { id: "risk",            label: "Risk" },
  { id: "psychology",      label: "Psychology" },
  { id: "strategy",        label: "Strategy" },
  { id: "discipline",      label: "Discipline" },
];

// Badge definitions — each has an eligibility check returning {earned, hint}.
const BADGES = [
  { id: "onboarded",    emoji: "🎯", name: "Onboarded",      hint: "Complete initial profile + diagnostic",
    earn: (ctx) => ctx.onboarded && ctx.diagnosticDone },
  { id: "first-trade",  emoji: "📓", name: "First Trade",    hint: "Log your first journal entry",
    earn: (ctx) => ctx.totalTrades >= 1 },
  { id: "a-grade",      emoji: "🅰️", name: "A-Grade Trader", hint: "Log 3 A-grade SuPER trades",
    earn: (ctx) => ctx.aGradeCount >= 3 },
  { id: "gate-master",  emoji: "🚪", name: "Gate Master",    hint: "Pass all 4 phase mastery gates",
    earn: (ctx) => ctx.gatesPassedCount >= 4 },
  { id: "streak-7",     emoji: "🔥", name: "Streak Keeper",  hint: "7-day login streak",
    earn: (ctx) => ctx.streakDays >= 7 },
  { id: "scholar",      emoji: "📚", name: "Lesson Scholar", hint: "Pass all Tier 1 module 1.1 + 1.2 lessons (9)",
    earn: (ctx) => ctx.lessonsPassed >= 9 },
  { id: "tier1-graduate", emoji: "🎓", name: "Tier 1 Graduate", hint: "Pass every lesson across modules 1.1 → 1.6",
    earn: (ctx) => ctx.tier1Graduated },
  { id: "capstone",     emoji: "🎓", name: "SuPER Certified", hint: "Issue your mastery capstone cert",
    earn: (ctx) => ctx.capstoneIssued },
  { id: "process",      emoji: "⚖️", name: "Process > Profit", hint: "Maintain a B+ weekly process grade",
    earn: (ctx) => ["A", "A+", "A-", "B+"].includes(ctx.weeklyProcessGrade) },
];

export default function ProfilePanel() {
  const profile = flexxState.profile();
  const graph = flexxState.graph();
  const xp = flexxState.xp();
  const streak = flexxState.streak();
  const gatesMap = flexxState.gates();
  const journal = flexxState.journal();
  const capstone = flexxState.capstone();
  const level = levelFor(xp);

  // NextGen Funding — read from localStorage; editable via a small form below.
  const [ngf, setNgf] = useState(() => {
    try { return JSON.parse(localStorage.getItem("flexx_ngf_status") || "null"); }
    catch (_) { return null; }
  });
  const [editNgf, setEditNgf] = useState(false);

  const ctx = useMemo(() => {
    const totalTrades = journal.length;
    const aGradeCount = journal.filter((e) => e.grade === "A").length;
    const fGradeCount = journal.filter((e) => e.grade === "F").length;
    const dGradeCount = journal.filter((e) => e.grade === "D").length;
    const gatesPassedCount = Object.values(gatesMap).filter((g) => g?.passed).length;
    const lessons = (() => {
      try { return JSON.parse(localStorage.getItem("flexx_lesson_completion") || "{}"); }
      catch (_) { return {}; }
    })();
    const lessonsPassed = Object.values(lessons).filter((v) => v?.passed).length;
    const tier1Graduated = !!localStorage.getItem("flexx_tier1_graduate");
    // Discipline score — derived from grade distribution + 7-day emotion hygiene
    const graded = journal.filter((e) => e.grade);
    const disciplineScore = graded.length === 0 ? 0 : Math.min(100, Math.round(
      (aGradeCount * 100 + (graded.filter((e) => e.grade === "B").length) * 75
       + (graded.filter((e) => e.grade === "C").length) * 50
       - dGradeCount * 25 - fGradeCount * 50) / graded.length + 50
    ));

    // Weekly process grade (rough — maps gate pass count ≥ certain thresholds)
    const weekMs = 7 * 86400000;
    const now = Date.now();
    const recent = graded.filter((e) => now - (e.createdAt || 0) <= weekMs);
    const map = { A: 4, B: 3, C: 2, D: 1, F: 0 };
    const inv = ["F", "D", "C", "B", "A"];
    const weeklyProcessGrade = recent.length === 0 ? "—"
      : (() => {
          const avg = recent.reduce((s, e) => s + (map[e.grade] ?? 0), 0) / recent.length;
          const rounded = Math.round(avg);
          return inv[rounded] + (avg - rounded >= 0.3 ? "+" : avg - rounded <= -0.3 ? "-" : "");
        })();

    return {
      onboarded: flexxState.isOnboarded(),
      diagnosticDone: flexxState.isDiagnosticDone(),
      totalTrades, aGradeCount, dGradeCount, fGradeCount,
      gatesPassedCount, streakDays: streak.days || 0,
      lessonsPassed, disciplineScore, weeklyProcessGrade,
      tier1Graduated,
      capstoneIssued: !!capstone?.certIssued || !!capstone?.certId,
    };
  }, [journal, gatesMap, streak, capstone]);

  const axisScores = useMemo(() => ({
    marketStructure: graph.marketStructure || 0,
    ta: graph.ta || 0,
    risk: graph.risk || 0,
    psychology: graph.psychology || 0,
    strategy: graph.strategy || 0,
    discipline: ctx.disciplineScore,
  }), [graph, ctx.disciplineScore]);

  const saveNgf = (next) => {
    localStorage.setItem("flexx_ngf_status", JSON.stringify(next));
    setNgf(next);
    setEditNgf(false);
  };

  return (
    <div data-testid="profile-panel" className="flexx-fade-in" style={{ display: "grid", gap: 16 }}>
      {/* Header card — identity + level */}
      <div className="flexx-card" data-testid="profile-identity" style={{
        borderLeft: `3px solid ${THEME.gold}`,
        display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 16, alignItems: "center",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 32,
          background: `linear-gradient(135deg, ${THEME.gold}, #E8B94F)`,
          color: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 28, fontWeight: 900, fontFamily: "'Bebas Neue', sans-serif",
        }}>{(profile.name || "T").charAt(0).toUpperCase()}</div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>TRADER PROFILE</div>
          <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, marginTop: 4 }}>
            {profile.name || "Trader"}
          </div>
          <div style={{ color: THEME.mute, fontSize: 12, marginTop: 4 }}>
            Trading <b style={{ color: THEME.text }}>{profile.market}</b> · {profile.experience} · {profile.studyTime}/day
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>LEVEL {level.level}</div>
          <div style={{ color: THEME.text, fontSize: 18, fontWeight: 800, marginTop: 2 }}>{level.title}</div>
          <div style={{ color: THEME.mute, fontSize: 11, marginTop: 4, fontFamily: "monospace" }}>{xp.toLocaleString()} XP</div>
        </div>
      </div>

      {/* 6-axis radar */}
      <div className="flexx-card" data-testid="profile-radar-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>SKILL RADAR (6 AXIS)</div>
          <div style={{ color: THEME.mute, fontSize: 11, fontFamily: "monospace" }}>
            Avg: {Math.round(Object.values(axisScores).reduce((a, b) => a + b, 0) / 6)}/100
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 340px) 1fr", gap: 20, alignItems: "center" }}>
          <SixAxisRadar scores={axisScores} />
          <div style={{ display: "grid", gap: 8 }}>
            {AXES.map((a) => {
              const v = axisScores[a.id];
              return (
                <div key={a.id} data-testid={`profile-axis-${a.id}`} style={{ display: "grid", gridTemplateColumns: "120px 1fr 40px", alignItems: "center", gap: 10 }}>
                  <div style={{ color: THEME.text, fontSize: 12, fontFamily: "monospace", letterSpacing: 0.5 }}>{a.label}</div>
                  <div style={{ height: 6, background: THEME.border, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${v}%`, height: "100%", background: a.id === "discipline" ? `linear-gradient(90deg, ${THEME.gold}, #E8B94F)` : THEME.gold, transition: "width 0.4s" }} />
                  </div>
                  <div style={{ color: THEME.gold, fontSize: 11, fontFamily: "monospace", textAlign: "right" }}>{v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="flexx-card" data-testid="profile-badges-card">
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 12 }}>
          BADGES · {BADGES.filter((b) => b.earn(ctx)).length} / {BADGES.length}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
          {BADGES.map((b) => {
            const earned = b.earn(ctx);
            return (
              <div key={b.id} data-testid={`profile-badge-${b.id}`}
                className={earned ? "flexx-badge-earned" : ""}
                style={{
                  padding: 12, borderRadius: 10,
                  background: earned ? `${THEME.gold}14` : "#0d1218",
                  border: `1px solid ${earned ? THEME.gold : THEME.border}`,
                  opacity: earned ? 1 : 0.55,
                  display: "flex", flexDirection: "column", gap: 6,
                }}>
                <div style={{ fontSize: 26 }}>{b.emoji}</div>
                <div style={{ color: earned ? THEME.text : THEME.mute, fontWeight: 700, fontSize: 13 }}>{b.name}</div>
                <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.4 }}>{b.hint}</div>
                <div style={{
                  marginTop: 4, fontSize: 9, fontFamily: "monospace", letterSpacing: 1.5,
                  color: earned ? "#22C55E" : THEME.mute,
                }}>{earned ? "✓ EARNED" : "LOCKED"}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Push Notifications opt-in + Install App */}
      <div className="flexx-card" data-testid="profile-push-card">
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 10 }}>APP & NOTIFICATIONS</div>
        <div style={{ display: "grid", gap: 10 }}>
          <InstallAppButton />
          <PushNotifications />
        </div>
      </div>

      {/* Active Sessions / device security */}
      <SecuritySessionsCard />

      {/* Change Password */}
      <ChangePasswordCard />

      {/* MT5 Execution Bridge */}
      <Mt5BridgePanel />

      {/* SageMaster AI Tools Hub */}
      <SageMasterHub unlocked={!!flexxState.tier2Graduate()} certified={!!flexxState.tier3Graduate()} />

      {/* Leaderboard */}
      <LeaderboardCard limit={20} />

      {/* NextGen Funding */}
      <div className="flexx-card" data-testid="profile-ngf-card" style={{ borderLeft: `3px solid ${THEME.gold}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>NEXTGEN FUNDING · CHALLENGE</div>
          <button data-testid="profile-ngf-edit-btn" onClick={() => setEditNgf((s) => !s)}
            style={{ fontSize: 11, padding: "4px 10px", background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.mute, borderRadius: 4, cursor: "pointer", fontFamily: "monospace" }}>
            {editNgf ? "Cancel" : (ngf ? "Edit" : "+ Set Up")}
          </button>
        </div>

        {editNgf ? (
          <NgfForm initial={ngf} onSave={saveNgf} />
        ) : ngf ? (
          <NgfView ngf={ngf} />
        ) : (
          <div style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6 }}>
            Not yet on a challenge. Click <b style={{ color: THEME.gold }}>+ Set Up</b> to track your NextGen Funding progress — account size, phase, days active, and circuit-breaker usage.
          </div>
        )}
      </div>
    </div>
  );
}

// ── 6-axis radar (SVG, hexagonal) ──────────────────────────────────
function deviceLabel(ua) {
  if (!ua) return "Unknown device";
  const os = /Windows/i.test(ua) ? "Windows" : /Mac OS X|Macintosh/i.test(ua) ? "macOS"
    : /iPhone|iPad|iOS/i.test(ua) ? "iOS" : /Android/i.test(ua) ? "Android"
    : /Linux/i.test(ua) ? "Linux" : "Unknown OS";
  const br = /Edg\//i.test(ua) ? "Edge" : /OPR\/|Opera/i.test(ua) ? "Opera"
    : /Chrome\//i.test(ua) ? "Chrome" : /Firefox\//i.test(ua) ? "Firefox"
    : /Safari\//i.test(ua) ? "Safari" : "Browser";
  return `${br} · ${os}`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function SecuritySessionsCard() {
  const { listSessions, signOutAllDevices } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);
  const gold = "#C9A227";

  useEffect(() => {
    let alive = true;
    listSessions().then((r) => { if (alive) setSessions(r.sessions); }).catch(() => { if (alive) setSessions([]); });
    return () => { alive = false; };
  }, [listSessions]);

  const signOutAll = async () => {
    if (!window.confirm("Sign out of ALL devices, including this one? You'll need to sign in again. Use this if you shared your login or lost a device.")) return;
    setBusy(true);
    await signOutAllDevices();
    // AuthContext sets user=false → app renders the auth screen automatically.
  };

  return (
    <div className="flexx-card" data-testid="security-sessions-card" style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#eef2f7", letterSpacing: ".04em" }}>🔒 Active Sessions</div>
        <span style={{ fontSize: 10.5, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1 }}>One device at a time</span>
      </div>
      <div style={{ fontSize: 11.5, color: "#6B7280", marginBottom: 14, lineHeight: 1.6 }}>
        Your FLEXX account is limited to a single active device. Signing in elsewhere ends this session automatically.
      </div>

      {sessions === null ? (
        <div style={{ color: "#6B7280", fontSize: 12.5 }} data-testid="security-sessions-loading">Loading…</div>
      ) : sessions.length === 0 ? (
        <div style={{ color: "#6B7280", fontSize: 12.5 }} data-testid="security-sessions-empty">No active session found.</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }} data-testid="security-sessions-list">
          {sessions.map((s) => (
            <div key={s.sid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: "#0D0F13", border: `1px solid ${s.this_device ? gold + "66" : "#1E222A"}` }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#eef2f7", fontSize: 13, fontWeight: 600 }}>
                  {deviceLabel(s.user_agent)}
                  {s.this_device && <span style={{ fontSize: 9.5, color: gold, border: `1px solid ${gold}`, borderRadius: 4, padding: "1px 6px", letterSpacing: ".06em" }}>THIS DEVICE</span>}
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                  IP {s.ip || "—"} · active {timeAgo(s.last_seen)} · since {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} title="Active" />
            </div>
          ))}
        </div>
      )}

      <button data-testid="sign-out-all-btn" onClick={signOutAll} disabled={busy}
        style={{ marginTop: 14, width: "100%", padding: "11px", background: "transparent", border: "1px solid #5a2a2e", borderRadius: 8, color: "#ff9aa6", fontSize: 12.5, fontWeight: 700, letterSpacing: ".04em", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Signing out…" : "Sign out of all devices"}
      </button>
    </div>
  );
}

function SixAxisRadar({ scores }) {
  const size = 280, cx = size / 2, cy = size / 2;
  const maxR = 110;
  const angles = Array.from({ length: 6 }, (_, i) => (Math.PI * 2 * i) / 6 - Math.PI / 2);
  const ringPct = [0.25, 0.5, 0.75, 1];
  const axisOrder = AXES.map((a) => a.id);

  const toPoint = (angle, r) => [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r];
  const hex = (r) => angles.map((a) => toPoint(a, r).join(",")).join(" ");
  const dataPts = angles.map((a, i) => {
    const v = scores[axisOrder[i]] || 0;
    return toPoint(a, (v / 100) * maxR).join(",");
  }).join(" ");

  return (
    <svg width={size} height={size} role="img" aria-label="6-axis skill radar" data-testid="profile-radar-svg"
      style={{ display: "block", margin: "0 auto" }}>
      {ringPct.map((p, i) => (
        <polygon key={i} points={hex(maxR * p)} fill="none" stroke={THEME.border} strokeWidth="1" />
      ))}
      {angles.map((a, i) => {
        const [x, y] = toPoint(a, maxR);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={THEME.border} strokeWidth="1" />;
      })}
      <polygon points={dataPts} fill={`${THEME.gold}33`} stroke={THEME.gold} strokeWidth="2" />
      {angles.map((a, i) => {
        const [x, y] = toPoint(a, maxR * 1.2);
        return (
          <text key={i} x={x} y={y} fill={THEME.text} fontSize="10" fontFamily="monospace"
            textAnchor="middle" dominantBaseline="middle" letterSpacing="1">
            {AXES[i].label}
          </text>
        );
      })}
    </svg>
  );
}

// ── NextGen Funding editor / viewer ───────────────────────────────
function NgfForm({ initial, onSave }) {
  const [form, setForm] = useState(() => initial || {
    accountSize: "10000",
    phase: "Evaluation",
    daysActive: 0,
    dailyLossUsedPct: 0,
    profitTargetPct: 10,
    profitCurrentPct: 0,
    payoutConsistencyPct: 0,
  });
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10 }}>
      <Field label="Account Size ($)">
        <input data-testid="ngf-account-size-input" type="number" value={form.accountSize} onChange={onChange("accountSize")} style={inputStyle} />
      </Field>
      <Field label="Phase">
        <select data-testid="ngf-phase-select" value={form.phase} onChange={onChange("phase")} style={inputStyle}>
          {["Evaluation", "Verification", "Funded", "Scaling"].map((p) => <option key={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="Days Active"><input type="number" data-testid="ngf-days-input" value={form.daysActive} onChange={onChange("daysActive")} style={inputStyle} /></Field>
      <Field label="Daily Loss Used (%)"><input type="number" step="0.1" data-testid="ngf-dl-input" value={form.dailyLossUsedPct} onChange={onChange("dailyLossUsedPct")} style={inputStyle} /></Field>
      <Field label="Profit Target (%)"><input type="number" step="0.1" data-testid="ngf-pt-input" value={form.profitTargetPct} onChange={onChange("profitTargetPct")} style={inputStyle} /></Field>
      <Field label="Profit Current (%)"><input type="number" step="0.1" data-testid="ngf-pc-input" value={form.profitCurrentPct} onChange={onChange("profitCurrentPct")} style={inputStyle} /></Field>
      <Field label="Biggest Day % of Total"><input type="number" step="0.1" data-testid="ngf-consistency-input" value={form.payoutConsistencyPct} onChange={onChange("payoutConsistencyPct")} style={inputStyle} /></Field>
      <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
        <button data-testid="ngf-save-btn" className="flexx-btn-primary" onClick={() => onSave({
          ...form,
          accountSize: Number(form.accountSize) || 0,
          daysActive: Number(form.daysActive) || 0,
          dailyLossUsedPct: Number(form.dailyLossUsedPct) || 0,
          profitTargetPct: Number(form.profitTargetPct) || 10,
          profitCurrentPct: Number(form.profitCurrentPct) || 0,
          payoutConsistencyPct: Number(form.payoutConsistencyPct) || 0,
        })}>Save</button>
      </div>
    </div>
  );
}

function NgfView({ ngf }) {
  const pct = Math.max(0, Math.min(100, (ngf.profitCurrentPct / ngf.profitTargetPct) * 100));
  const dlColor = ngf.dailyLossUsedPct >= 4 ? "#FF4444" : ngf.dailyLossUsedPct >= 2 ? "#FFB347" : "#22C55E";
  const consistencyWarn = ngf.payoutConsistencyPct > 30;
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        <Stat label="Account" value={`$${ngf.accountSize.toLocaleString()}`} color={THEME.text} />
        <Stat label="Phase" value={ngf.phase} color={THEME.gold} />
        <Stat label="Days Active" value={ngf.daysActive} color={THEME.text} />
        <Stat label="Daily Loss Used" value={`${ngf.dailyLossUsedPct}%`} color={dlColor} testid="ngf-dl-stat" />
        <Stat label="Consistency (biggest day)" value={`${ngf.payoutConsistencyPct}%`} color={consistencyWarn ? "#FF6B35" : "#22C55E"} />
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontFamily: "monospace", color: THEME.mute, marginBottom: 6 }}>
          <span>PROFIT PROGRESS</span>
          <span>{ngf.profitCurrentPct}% / {ngf.profitTargetPct}%</span>
        </div>
        <div style={{ height: 10, background: THEME.border, borderRadius: 5, overflow: "hidden" }}>
          <div data-testid="ngf-profit-bar" style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${THEME.gold}, #E8B94F)`, transition: "width 0.4s" }} />
        </div>
      </div>
      {ngf.dailyLossUsedPct >= 4 && (
        <div style={{ padding: 10, background: "#FF444422", border: "1px solid #FF4444", borderRadius: 8, color: "#FF8888", fontSize: 12 }}>
          ⚠️ Daily loss ≥ 4% — CIRCUIT BREAKER. Close platform. Walk. Tomorrow is a new day.
        </div>
      )}
      {consistencyWarn && (
        <div style={{ padding: 10, background: "#FF6B3522", border: "1px solid #FF6B35", borderRadius: 8, color: "#FFB88C", fontSize: 12 }}>
          Biggest single-day P&L is {ngf.payoutConsistencyPct}% of total. Keep under 30% for the payout consistency rule.
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ color: THEME.mute, fontSize: 11, letterSpacing: 0.5, marginBottom: 6, textTransform: "uppercase" }}>{label}</div>
      {children}
    </label>
  );
}
function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ background: "#0d1218", border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ color: THEME.mute, fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: color || THEME.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>{value}</div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10,
  background: "#13161c", border: `1px solid ${THEME.border}`, color: THEME.text, fontSize: 13,
  outline: "none", boxSizing: "border-box",
};

// ── SageMaster Tools Hub ─────────────────────────────────────────
function SageMasterHub({ unlocked, certified }) {
  return (
    <div data-testid="profile-sagemaster-hub" className="flexx-card" style={{
      borderLeft: `3px solid ${unlocked ? TIER3_EMERALD : THEME.border}`,
      boxShadow: unlocked ? `0 0 24px rgba(16,185,129,0.10)` : "none",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: "monospace", color: unlocked ? TIER3_EMERALD : THEME.mute, fontSize: 10, letterSpacing: 2 }}>
            {unlocked ? "🤖 SAGEMASTER AI TOOLS" : "🔒 SAGEMASTER AI TOOLS"}
          </div>
          <div style={{ color: THEME.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>
            9-Tool AI Ecosystem
          </div>
          <div style={{ color: THEME.mute, fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
            {unlocked
              ? "Tier 3 unlocked. Explore SageMaster's full toolset and apply what AXEL teaches."
              : "Earn SuPER CERTIFIED (Tier 2) to unlock the SageMaster ecosystem."}
          </div>
        </div>
        {certified && (
          <span data-testid="sagemaster-hub-certified" style={{
            padding: "4px 10px", border: `1px solid ${TIER3_EMERALD}`, borderRadius: 999,
            color: TIER3_EMERALD, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5,
            background: `${TIER3_EMERALD}15`,
          }}>
            🤖 AI TRADER
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
        {SAGEMASTER_TOOLS.map((t) => {
          const accent = unlocked ? TIER3_EMERALD : THEME.border;
          const cardStyle = {
            padding: 12, borderRadius: 10,
            background: unlocked ? `${TIER3_EMERALD}10` : "#0d1218",
            border: `1px solid ${accent}${unlocked ? "" : "55"}`,
            display: "flex", flexDirection: "column", gap: 6,
            opacity: unlocked ? 1 : 0.55,
            textDecoration: "none",
            transition: "all 0.25s",
          };
          const inner = (
            <>
              <div style={{ fontSize: 22, lineHeight: 1 }}>{unlocked ? t.icon : "🔒"}</div>
              <div style={{ color: unlocked ? THEME.text : THEME.mute, fontWeight: 700, fontSize: 13 }}>{t.name}</div>
              <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.4 }}>{t.desc}</div>
              <div style={{
                marginTop: 4, fontSize: 9, fontFamily: "monospace", letterSpacing: 1.5,
                color: unlocked ? TIER3_EMERALD : THEME.mute,
              }}>
                {unlocked ? "OPEN ↗" : "LOCKED"}
              </div>
            </>
          );
          if (unlocked) {
            return (
              <a key={t.id} data-testid={`sagemaster-tool-${t.id}`}
                href={SAGEMASTER_URL} target="_blank" rel="noopener noreferrer"
                style={cardStyle}>
                {inner}
              </a>
            );
          }
          return (
            <div key={t.id} data-testid={`sagemaster-tool-${t.id}`}
              data-locked="true" aria-disabled="true" style={cardStyle}>
              {inner}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.5 }}>
          {unlocked
            ? "Subscription required to access the live tools — AXEL guides you on which tool to use, when."
            : "All 9 tools live inside one ecosystem · Subscription required."}
        </div>
        <a data-testid="sagemaster-subscribe-link" href={SAGEMASTER_URL} target="_blank" rel="noopener noreferrer"
          style={{
            padding: "8px 14px", borderRadius: 6,
            background: unlocked ? TIER3_EMERALD : "transparent",
            color: unlocked ? "#0a0a0a" : TIER3_EMERALD,
            border: `1px solid ${TIER3_EMERALD}`,
            fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5,
            textDecoration: "none", fontWeight: 800,
          }}>
          {unlocked ? "OPEN SAGEMASTER ↗" : "LEARN HOW TO SUBSCRIBE ↗"}
        </a>
      </div>
    </div>
  );
}
